import type { GameState, TileState } from '../types';
import { COLS, ROWS } from '../game/constants';

// Goo-filtered blob structure for per-tile bleed. The SVG #goo filter in
// index.html fuses overlapping circles into organic teardrop shapes when the
// tile carries the .absent class.
const BLEED_TEMPLATE = `
  <div class="liquid"><svg class="liquid-edge" viewBox="0 0 60 30" preserveAspectRatio="none" aria-hidden="true"><path d=""></path></svg></div>
  <div class="blob"></div>
  <div class="blob"></div>
  <div class="blob"></div>
  <div class="blob"></div>
  <div class="blob"></div>
`;

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

// Per-tile drip jitter — wide ranges so the row doesn't read as a stamp.
// Inline styles override the class rules in style.css; left ranges are
// clamped so the two drops stay on opposite halves. The curtain's initial
// wet-line height is driven by --liquid-from, which the `liquidPour`
// keyframe reads (var() in keyframes is honored by modern browsers).
function randomizeBleed(drips: HTMLElement): void {
  const liquid = drips.querySelector<HTMLElement>('.liquid');
  if (liquid) {
    liquid.style.animationDelay = `${rand(0, 1.3).toFixed(2)}s`;
    liquid.style.animationDuration = `${rand(1.8, 4.0).toFixed(2)}s`;
    liquid.style.left = `${rand(-18, -6).toFixed(1)}%`;
    liquid.style.width = `${rand(108, 132).toFixed(0)}%`;
    liquid.style.setProperty('--liquid-from', `${rand(3, 11).toFixed(1)}px`);
  }
  const edge = drips.querySelector<SVGPathElement>('.liquid-edge path');
  if (edge) edge.setAttribute('d', wavePath());
  const blobs = drips.querySelectorAll<HTMLElement>('.blob');
  const left = blobs[1];
  if (left) {
    left.style.left = `${Math.round(rand(6, 42))}%`;
    left.style.width = `${rand(6, 11).toFixed(1)}px`;
    left.style.height = `${rand(8, 13).toFixed(1)}px`;
    left.style.animationDelay = `${rand(0, 0.9).toFixed(2)}s`;
    left.style.animationDuration = `${rand(1.9, 3.2).toFixed(2)}s`;
    left.style.borderRadius = teardrop();
  }
  const right = blobs[3];
  if (right) {
    right.style.left = `${Math.round(rand(56, 92))}%`;
    right.style.width = `${rand(6, 11).toFixed(1)}px`;
    right.style.height = `${rand(8, 13).toFixed(1)}px`;
    right.style.animationDelay = `${rand(0.5, 1.8).toFixed(2)}s`;
    right.style.animationDuration = `${rand(2.2, 3.6).toFixed(2)}s`;
    right.style.borderRadius = teardrop();
  }
}

function teardrop(): string {
  const top = Math.round(rand(45, 70));
  const bot = Math.round(rand(25, 45));
  return `50% 50% 50% 50% / ${top}% ${top}% ${bot}% ${bot}%`;
}

// Wavy SVG path that hangs off the curtain's bottom edge. Built right-to-left
// so the last quadratic segment closes cleanly back at (0,0). Each bump has
// its own depth so the wave isn't a uniform sine. viewBox is 60x30; bump
// depths span ~18-28 units, well over the goo filter's blur radius so the
// shape survives merging with the curtain above it.
function wavePath(): string {
  const w = 60;
  const segments = 3 + Math.floor(Math.random() * 3);
  const segW = w / segments;
  let d = `M0,0 L${w},0`;
  for (let i = 0; i < segments; i++) {
    const xStart = w - i * segW;
    const xEnd = w - (i + 1) * segW;
    const xCtrl = (xStart + xEnd) / 2;
    const depth = rand(18, 28);
    const yEnd = i === segments - 1 ? 0 : rand(3, 7);
    d += ` Q${xCtrl.toFixed(1)},${depth.toFixed(1)} ${xEnd.toFixed(1)},${yEnd.toFixed(1)}`;
  }
  return d + ' Z';
}

export function buildBoard(root: HTMLElement): void {
  root.innerHTML = '';
  for (let r = 0; r < ROWS; r++) {
    const row = document.createElement('div');
    row.className = 'row';
    row.dataset['row'] = String(r);
    for (let c = 0; c < COLS; c++) {
      const tile = document.createElement('div');
      tile.className = 'tile';
      tile.dataset['col'] = String(c);

      const letter = document.createElement('span');
      letter.className = 'tile-letter';
      tile.appendChild(letter);

      const drips = document.createElement('div');
      drips.className = 'bleed-blobs';
      drips.setAttribute('aria-hidden', 'true');
      drips.innerHTML = BLEED_TEMPLATE;
      tile.appendChild(drips);
      randomizeBleed(drips);

      row.appendChild(tile);
    }
    root.appendChild(row);
  }
}

export function renderBoard(root: HTMLElement, state: GameState): void {
  const rows = root.querySelectorAll<HTMLElement>('.row');
  for (let r = 0; r < ROWS; r++) {
    const row = rows[r];
    if (!row) continue;
    const tiles = row.querySelectorAll<HTMLElement>('.tile');
    const guess = state.guesses[r];
    if (guess) {
      for (let c = 0; c < COLS; c++) {
        const tile = tiles[c];
        if (!tile) continue;
        const letter = tile.querySelector<HTMLElement>('.tile-letter');
        if (letter) letter.textContent = guess.word[c] ?? '';
        const result = guess.results[c];
        if (result) tile.classList.add('filled', result);
      }
    } else if (r === state.guesses.length && state.status === 'playing') {
      // Only paint the active input row while the game is still on. Once the
      // game is over, leftover state.current must never leak onto the board.
      for (let c = 0; c < COLS; c++) {
        const tile = tiles[c];
        if (!tile) continue;
        const letter = tile.querySelector<HTMLElement>('.tile-letter');
        const ch = state.current[c] ?? '';
        if (letter) letter.textContent = ch;
        tile.classList.toggle('filled', !!ch);
      }
    }
  }
}

export function revealRow(root: HTMLElement, rowIdx: number, results: readonly TileState[]): void {
  const row = root.querySelectorAll<HTMLElement>('.row')[rowIdx];
  if (!row) return;
  const tiles = row.querySelectorAll<HTMLElement>('.tile');
  for (let i = 0; i < COLS; i++) {
    const tile = tiles[i];
    const result = results[i];
    if (!tile || !result) continue;
    // Staggered reveal — each tile picks up the result class 100ms after the
    // previous. The existing .tile transition (background/color/border over
    // 0.4s) handles the visual sweep; no rotation needed.
    setTimeout(() => tile.classList.add(result), i * 100);
  }
}

export function shakeRow(root: HTMLElement, rowIdx: number): void {
  const row = root.querySelectorAll<HTMLElement>('.row')[rowIdx];
  if (!row) return;
  row.classList.add('shake');
  setTimeout(() => row.classList.remove('shake'), 500);
}
